/**
 * News — what is new in OpenComply, release by release.
 *
 * Each post reads the way the product owner wrote his example:
 *
 *   Version 1.24.11 of OpenComply has been released. <summary>
 *
 *   New features:
 *   * Audit: <body>
 *   Quality of life:
 *   * ...
 *   Bug fixes:
 *   * ...
 *
 * WHAT IS TRANSLATED AND WHAT IS NOT
 * The chrome (the sentence "Version X … has been released", the category
 * headings, dates, the "New" badge) goes through i18next, EN and EL. The post
 * text itself (headline, summary, entry titles and bodies) is authored English
 * stored in the database and is shown as written. Translating every future post
 * is a burden nobody has asked for, so the page says so, in Greek, rather than
 * leaving a Greek reader to wonder why the paragraphs did not follow.
 *
 * TEXT, NEVER HTML
 * Every piece of post text is rendered as a React text node. There is no
 * dangerouslySetInnerHTML on this page and the API sends no markup, so there is
 * nothing to sanitise and nothing for a stored post to inject.
 *
 * MARKING IT SEEN
 * Once the first page has loaded, the page tells the server the news has been
 * seen, and writes a cleared count into the nav indicator's cache so the dot
 * goes at once. The list keeps showing which releases were new on THIS visit —
 * it is not refetched after marking — and `gcTime: 0` stops a cached copy from
 * reappearing with stale "New" badges on the next visit.
 */

import { useEffect, useRef } from 'react';
import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  RELEASE_CATEGORIES, RELEASE_PAGE_SIZE,
  type ReleaseCategory, type ReleaseKind,
} from '@opencomply/shared';
import { api, qs } from '../lib/api';
import { useAuth } from '../lib/auth';
import { cn } from '../lib/cn';
import { Card, Button, Pill, EmptyState, Skeleton, ErrorNotice, type Tone } from '../components/ui';
import { IconNews, newsUnreadKey, type NewsUnread } from '../components/NewsNav';
import { useNewsT } from '../lib/newsStrings';

interface ReleaseEntry {
  category: ReleaseCategory;
  title: string;
  body: string;
}

interface Release {
  version: string;
  kind: ReleaseKind;
  releasedAt: string;
  headline: string;
  summary: string;
  entries: ReleaseEntry[];
  publishedAt: string;
  isNew: boolean;
}

interface ReleasePage {
  items: Release[];
  /** The caller's marker when the request began; null when never seen. */
  lastSeenAt: string | null;
  total: number;
  page: number;
  pageSize: number;
}

const KIND_TONE: Record<ReleaseKind, Tone> = {
  MAJOR: 'serious',
  MINOR: 'neutral',
  PATCH: 'neutral',
};

export function NewsPage() {
  const { tx, formatDate, language } = useNewsT();
  const { session } = useAuth();
  const queryClient = useQueryClient();
  const userId = session?.user.id;

  const query = useInfiniteQuery({
    queryKey: ['news', 'releases'],
    initialPageParam: 1,
    queryFn: ({ pageParam }) =>
      api.get<ReleasePage>(`/news${qs({ page: pageParam, pageSize: RELEASE_PAGE_SIZE })}`),
    getNextPageParam: (last) =>
      last.page * last.pageSize < last.total ? last.page + 1 : undefined,
    // See the header: never show a cached copy's out-of-date "New" badges.
    gcTime: 0,
    refetchOnWindowFocus: false,
  });

  const markSeen = useMutation({
    mutationFn: () => api.post<{ seenAt: string }>('/news/seen'),
    onSuccess: () => {
      // Clear the nav dot now rather than at the next poll.
      queryClient.setQueryData<NewsUnread>(newsUnreadKey(userId), (old) => ({
        count: 0,
        latestVersion: old?.latestVersion ?? null,
      }));
    },
  });

  // Once per mount, and only after the list has actually been shown: marking
  // something seen that failed to load would hide news nobody read.
  const marked = useRef(false);
  useEffect(() => {
    if (query.isSuccess && !marked.current) {
      marked.current = true;
      markSeen.mutate();
    }
    // `markSeen` is a stable mutation object; depending on it would re-run.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query.isSuccess]);

  const fetchNext = () => void query.fetchNextPage();
  const pages = query.data?.pages ?? [];
  // "New" is judged against the marker as it stood on the FIRST page, which was
  // fetched before this visit marked the news seen. Judging each later page by
  // its own response would lose the badge on exactly the older posts a returning
  // reader scrolls down to.
  const baseline = pages[0]?.lastSeenAt ?? null;
  const releases = pages
    .flatMap((p) => p.items)
    .map((r) => ({ ...r, isNew: baseline === null || r.publishedAt > baseline }));
  const total = pages[0]?.total ?? 0;

  // Deep link: /news#v0.10.0 scrolls to that release once it is on screen.
  useEffect(() => {
    if (!query.isSuccess || !window.location.hash) return;
    document.getElementById(decodeURIComponent(window.location.hash.slice(1)))
      ?.scrollIntoView({ block: 'start' });
  }, [query.isSuccess, releases.length]);

  return (
    <div className="oc-page space-y-5 animate-fade-in">
      <header>
        <h1 className="oc-h1">{tx('news.title')}</h1>
        <p className="oc-muted mt-1">{tx('news.subtitle')}</p>
        {language.startsWith('el') && (
          <p className="text-xs text-ink-muted mt-1">{tx('news.englishNote')}</p>
        )}
      </header>

      {query.isPending && (
        <div className="space-y-4" aria-busy="true">
          <Skeleton className="h-64" />
          <Skeleton className="h-48" />
        </div>
      )}

      {query.isError && releases.length === 0 && (
        <ErrorNotice
          message={tx('news.loadFailed')}
          onRetry={() => void query.refetch()}
          retryLabel={tx('news.retry')}
        />
      )}

      {query.isSuccess && releases.length === 0 && (
        <Card>
          <EmptyState
            icon={<IconNews className="w-8 h-8" />}
            title={tx('news.empty.title')}
            description={tx('news.empty.body')}
          />
        </Card>
      )}

      {releases.map((release, index) => (
        <ReleasePost
          key={release.version}
          release={release}
          isLatest={index === 0}
          tx={tx}
          formatDate={formatDate}
        />
      ))}

      {releases.length > 0 && (
        <div className="text-center space-y-2 pt-1 pb-4">
          <p className="oc-muted" aria-live="polite">
            {tx('news.showing', { count: releases.length, total })}
          </p>
          {query.hasNextPage ? (
            <Button
              onClick={fetchNext}
              disabled={query.isFetchingNextPage}
            >
              {query.isFetchingNextPage ? tx('news.loadingMore') : tx('news.loadMore')}
            </Button>
          ) : (
            <p className="text-sm text-ink-muted">{tx('news.endOfHistory')}</p>
          )}
          {query.isFetchNextPageError && (
            <ErrorNotice
              message={tx('news.loadFailed')}
              onRetry={fetchNext}
              retryLabel={tx('news.retry')}
            />
          )}
        </div>
      )}
    </div>
  );
}

function ReleasePost({ release, isLatest, tx, formatDate }: {
  release: Release;
  isLatest: boolean;
  tx: ReturnType<typeof useNewsT>['tx'];
  formatDate: (isoDate: string) => string;
}) {
  // Group in the fixed category order; the API already sorts, but the page does
  // not rely on it for the HEADINGS.
  const groups = RELEASE_CATEGORIES
    .map((category) => ({
      category,
      entries: release.entries.filter((e) => e.category === category),
    }))
    .filter((g) => g.entries.length > 0);

  const headingId = `v${release.version}`;

  return (
    <article id={headingId} aria-labelledby={`${headingId}-title`} className="scroll-mt-4">
      <Card className="space-y-4">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-mono text-sm font-semibold text-brand bg-brand/10 rounded-lg px-2 py-0.5">
            {release.version}
          </span>
          <Pill tone={KIND_TONE[release.kind]}>{tx(`news.kind.${release.kind}` as const)}</Pill>
          {isLatest && <Pill tone="good">{tx('news.latest')}</Pill>}
          {release.isNew && <Pill tone="warning">{tx('news.new')}</Pill>}
          <time
            dateTime={release.releasedAt}
            className="ml-auto text-sm text-ink-secondary tabular-nums"
          >
            {tx('news.released', { date: formatDate(release.releasedAt) })}
          </time>
        </div>

        <h2 id={`${headingId}-title`} className="oc-h2 text-balance">{release.headline}</h2>

        <div className="space-y-2 text-sm text-ink leading-relaxed">
          <p>
            <strong>{tx('news.announce', { version: release.version })}</strong>{' '}
            {/* Plain text. Never HTML: see the header. */}
            {release.summary}
          </p>
        </div>

        {groups.map(({ category, entries }) => (
          <section key={category} aria-labelledby={`${headingId}-${category}`}>
            <h3
              id={`${headingId}-${category}`}
              className={cn(
                'text-sm font-semibold mb-1.5',
                category === 'FIX' ? 'text-ink-secondary' : 'text-ink',
              )}
            >
              {tx(`news.category.${category}` as const)}:
            </h3>
            <ul className="space-y-2 text-sm text-ink leading-relaxed list-disc pl-5 marker:text-ink-muted">
              {entries.map((entry) => (
                <li key={entry.title}>
                  <span className="font-medium">{entry.title}:</span>{' '}
                  <span className="whitespace-pre-line">{entry.body}</span>
                </li>
              ))}
            </ul>
          </section>
        ))}
      </Card>
    </article>
  );
}
